import * as React from "react"
import { VTableRow } from "@digits-shared/components/UI/VirtualizedTable/VTableRow"
import { type VTableBodyProps } from "@digits-shared/components/UI/VirtualizedTable/vTableTypes"
import { Tbody } from "@digits-shared/DesignSystem/Tables/tableStyles"
import useScrollContext from "@digits-shared/hooks/useScrollContext"
import { useStableArray } from "@digits-shared/hooks/useStableArray"
import { useVirtualizer } from "@tanstack/react-virtual"

// Use a NOOP for scrollToFn to disable TanStack Virtual's programmatic scrolling.
// This prevents the virtualizer from scrolling the container automatically (e.g., when calling scrollToIndex/scrollToOffset),
// which was causing unwanted page scroll behavior in our context. User-driven scrolling (mouse, trackpad, scrollbar) still works.
// Implication: Any programmatic scroll requests from the virtualizer will be ignored, so jumping to a row via API will not scroll.
const SCROLL_NOOP = () => {}

/*
  COMPONENTS
*/
export const VTableBody = <TData extends object>({
  className,
  ref,
  table,
  estimateRowSize,
  tableRef,
  loadingInPlace,
}: VTableBodyProps<TData>) => {
  const { rows } = table.getRowModel()
  const { scrollElement } = useScrollContext()

  const scrollMargin = calculateScrollMargin(tableRef, scrollElement)
  const rowVirtualizer = useVirtualizer({
    estimateSize: estimateRowSize,
    getScrollElement: () => scrollElement.current,
    count: rows.length,
    scrollMargin,
    overscan: 150,
    useAnimationFrameWithResizeObserver: true,
    scrollToFn: SCROLL_NOOP,
  })

  const virtualRows = rowVirtualizer.getVirtualItems()
  const vRowsLength = virtualRows.length
  const vRowsStart = virtualRows[0]?.start ?? 0
  const vRowsEnd = virtualRows[vRowsLength - 1]?.end ?? 0
  const totalSize = rowVirtualizer.getTotalSize()

  // Use the stable array hook to prevent unnecessary recalculations
  const vRowsIndices = useStableArray(virtualRows.map((v) => v.index))

  // triggers rows re-renders if the selection state changes
  const selectionState = table.getState().rowSelection
  const columnDefs = useStableArray(table.getAllColumns().map((c) => c.columnDef))

  const { onRowClick, getRowClassName } = table.options.meta ?? {}
  const { paddingTop, paddingBottom, virtualRowElements } = React.useMemo(() => {
    // Force re-render when these values change to ensure proper row rendering
    const _ = [columnDefs]

    const paddingTop = vRowsLength ? vRowsStart - scrollMargin : 0
    const paddingBottom = vRowsLength ? scrollMargin + totalSize - vRowsEnd : 0
    const virtualRowElements = vRowsIndices.map((virtualIndex) => {
      const row = rows[virtualIndex]
      if (!row) return null
      const rowClassName = getRowClassName?.(row.original, virtualIndex)

      return (
        <VTableRow
          key={row.id}
          rowClassName={rowClassName}
          virtualIndex={virtualIndex}
          row={row}
          cells={row.getVisibleCells()}
          selected={selectionState[row.id]}
          onRowClick={onRowClick}
        />
      )
    })

    return { paddingTop, paddingBottom, virtualRowElements }
  }, [
    columnDefs,
    vRowsLength,
    vRowsStart,
    scrollMargin,
    totalSize,
    vRowsEnd,
    vRowsIndices,
    rows,
    selectionState,
    onRowClick,
    getRowClassName,
  ])

  const paddingColumns = table.getAllLeafColumns().length

  return (
    <Tbody
      ref={ref}
      css={loadingInPlace ? "display: none" : ""}
      inert={rowVirtualizer.isScrolling}
      className={className}
    >
      <PaddingRows paddingColumns={paddingColumns} padding={paddingTop} />
      {virtualRowElements}
      <PaddingRows paddingColumns={paddingColumns} padding={paddingBottom} />
    </Tbody>
  )
}

const PaddingRows: React.FC<{ paddingColumns: number; padding: number }> = React.memo(
  ({ paddingColumns, padding }) => {
    if (!padding) return null
    return (
      <tr>
        <td colSpan={paddingColumns} style={{ height: `${padding}px` }} />
      </tr>
    )
  }
)

function calculateScrollMargin(
  tableRef: React.RefObject<HTMLElement | null>,
  scrollElement: React.RefObject<HTMLElement | null>
) {
  if (!tableRef.current || !scrollElement.current) return 0

  let element: HTMLElement | null = tableRef.current
  let totalOffset = 0

  // Traverse up the DOM tree until we reach the scroll element
  while (element && element !== scrollElement.current) {
    totalOffset += element.offsetTop
    element = element.offsetParent as HTMLElement
  }

  return totalOffset
}
